import Cookies from 'js-cookie';
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import storage from "./storageFirebase";
import auth from "./authentication";
import { getFileUrl, TORRESOWO_URL } from "./consts";
import groupDefault from "../images/groupdefault.png";
import { TorresstreamApi } from "../apis/torrestream-api/torresstream-api";

export const goHomeAfterLogin = () => {
    return !window.location.pathname.includes("group-details") && !window.location.pathname.includes("shared-files")
        && !window.location.pathname.includes("squadflix")
}

export function random16Bytes() {
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    return b;
}

export function uuidBase64Url() {
    const b = random16Bytes();
    // version/variant like v4 (optional but recommended)
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;

    const bin = String.fromCharCode(...b);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export const getAudioFromMetadata = (metadata) => {
    if (!metadata?.streams) {
        return [];
    }

    const audios = metadata.streams.filter(x => x.codec_type === 'audio');

    return audios.filter(x => x.tags?.language).map(x => {
        const profile = x.profile == 'unknown' ? x.codec_name : x.profile;

        return {
            id: x.index,
            language: x.tags.language,
            label: x.tags.title ?? profile,
        }
    });
}

export const getSubtitlesFromMetadata = (metadata) => {

    if (!metadata?.streams) {
        return [];
    }

    const subtitles = metadata.streams.filter(x => x.codec_type === 'subtitle');

    return subtitles.filter(x => x.tags?.language).map(x => {
        return {
            id: x.index,
            lang: x.tags.language,
            title: x.tags.title,
            url: TorresstreamApi.get('streamSubtitles').getUrl({
                videoPath: metadata.format.filename,
                streamIndex: x.index
            })
        }
    });
}

export const getDurationFromMetadata = (metadata, item) => {
    let parsedDuration = metadata.duration ? metadata.duration / (1000 * 60) : undefined;
    if (!parsedDuration) {
        parsedDuration = metadata.format?.duration ? (metadata.format.duration / 60) : undefined;
    }
    if (!parsedDuration) {
        parsedDuration = item?.runtime ?? 0;
    }

    return parsedDuration;
}

export const hasClass = (element, classNames) => {
    if (classNames.some(x => element.classList.contains(x))) {
        return true;
    }
    if (!element.parentElement) {
        return false;
    }

    return hasClass(element.parentElement, classNames);
}

export const dateIsToday = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date.getTime() < today.getTime();
}

export const getGroupPhotoUrl = (group) => {
    if (group.photoUrl?.startsWith("/static/media/groupdefault")) {
        return groupDefault;
    }

    return group.photoUrl;
}

export const getLastModifiedMessage = (intl, lastModifiedDateString) => {
    if (!lastModifiedDateString) {
        return null;
    }

    const date = new Date(lastModifiedDateString);
    return intl.formatMessage({ id: 'tmdb-notepad.modal-title-last-modified' }, { date: date.toLocaleString() });
}

export const getViewMessage = (intl, viewDateString) => {
    if (!viewDateString) {
        return null;
    }

    const date = new Date(viewDateString);
    return intl.formatMessage({ id: 'tmdb-item-details.view-date' }, { date: date.toLocaleString() });
}

export function cn(...args) {
    const stringArgs = args.filter(x => typeof x === 'string' && x.trim() !== '');
    const objectArgs = args.filter(x => typeof x === 'object' && x !== null);

    const objectClasses = objectArgs.flatMap(obj => Object.entries(obj)
        .filter(([key, value]) => value)
        .map(([key]) => key));
    return [...stringArgs, ...objectClasses].join(' ');
}

export function formatFileSize(fileSize, useGB = false) {
    if (fileSize < 1024 && !useGB) {
        return fileSize.toFixed(2) + ' bytes';
    } else if (fileSize < 1024 * 1024 && !useGB) {
        return (fileSize / 1024).toFixed(2) + ' KB';
    } else if (fileSize < 1024 * 1024 * 1024 && !useGB) {
        return (fileSize / (1024 * 1024)).toFixed(2) + ' MB';
    } else {
        return (fileSize / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    }
}

export function formatDate(dateString, useSeconds = false, useHours = true,) {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    if (useSeconds) {
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } else if (useHours) {
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } else {
        return `${day}/${month}/${year}`;
    }
}

export function validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

export function getFileExtension(filename) {
    // Get the last index of the dot character
    const dotIndex = filename.lastIndexOf('.');

    // Extract the file extension
    if (dotIndex >= 0 && dotIndex < filename.length - 1) {
        return filename.substring(dotIndex + 1);
    } else {
        return '';
    }
}

export function getFileName(filename) {

    const dotIndex = filename.lastIndexOf('.');

    // Extract the file name
    if (dotIndex >= 0) {
        return filename.substring(0, Math.min(dotIndex, 60));
    } else {
        return filename;
    }
}

export function fileViewerSupported(file) {
    if (!file?.fileType) {
        return false;
    }
    if (file.fileType.includes('audio') || file.fileType.includes('video') || file.fileType.includes('image') || file.fileType.includes('pdf')) {
        return true;
    }
}

export function getIconOfName(file) {
    if (file.fileType.includes('text')) {
        return "fa-regular fa-file-lines";
    }
    const extension = getFileExtension(file.fileName).toLowerCase();
    switch (extension) {
        case "pdf":
            return "fa-regular fa-file-pdf";
        case "zip":
        case "rar":
            return "fa-regular fa-file-zipper";
        case "mkv":
            return "fa-regular fa-file-video";
        default:
            return "fa-regular fa-file";
    }
}


export const checkNode = (node, tokens) => {
    if (!node.classList) {
        return false;
    }
    const check = tokens.some(x => node.classList.contains(x));
    if (check) {
        return check;
    } else {
        return checkNode(node.parentNode, tokens);
    }

}

export function isMediumScreen() {
    return window.innerWidth < 992;
}

export function isSmallMediumScreen() {
    return window.innerWidth < 768;
}

export function isSmallScreen() {
    return window.innerWidth < 500;
}

export function isMobileDevice() {
    return window.innerWidth < 1200;
}

export function isOnlyMobileDevice() {
    const userAgent = navigator.userAgent;
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i;

    return mobileRegex.test(userAgent);
}

export function isSmartTvDevice(): boolean {
    const userAgent = navigator.userAgent || "";
    const tvRegex = /SmartTV|Smart-TV|Tizen|Web0S|WebOS|NetCast|Viera|BRAVIA|AFTS|AFTM|AFTB|AFTT|AFTN|FireTV|GoogleTV|Android TV|AppleTV|HbbTV|CrKey|Large Screen|PhilipsTV|Hisense|VIDAA/i;

    return tvRegex.test(userAgent);
}

export const getBackgroundColor = (darkMode, opacity) => {
    const opacityHex = opacity >= 0 ? Math.floor(opacity * 255).toString(16).padStart(2, '0') : "FF";
    return (darkMode ? "#212529" : "#FFFFFF") + opacityHex;
}

export const simpleDownloadFile = (fileurl: string, fileName: string) => {
    // Create a temporary anchor element
    window.open(fileurl + "?withName=" + fileName + "&disposition=attachment", '_blank');
}

export const downloadFile = async (file, onProgress, onError, signal, onCancel) => {
    if (file.storeUrl.startsWith("http")) {
        simpleDownloadFile(file.storeUrl, file.fileName);
        return;
    }

    try {
        let response = null;
        if (signal) {
            response = await fetch(getFileUrl(file), { signal });
        } else {
            response = await fetch(getFileUrl(file));
        }
        const contentLength = +response.headers.get('Content-Length');
        const reader = response.body.getReader();
        let canceled = false;
        let finished = false;

        const stream = new ReadableStream({
            start(controller) {
                let startTime = performance.now(); // Tiempo inicial
                let receivedLength = 0;
                let chunks = [{ time: startTime, length: 0 }];
                let lastSpeed = 0;

                const calculateSpeed = () => {
                    const now = performance.now();
                    const lengthChunks = chunks.reduce((acc, chunk, index) => {
                        if (index === 0) return acc;
                        return acc + chunk.length;
                    }, 0);

                    lastSpeed = (lengthChunks / (now - chunks[0].time)) * 1000;
                    if (!finished && !canceled) {
                        if (onProgress) onProgress(file, (receivedLength / contentLength) * 100, lastSpeed);
                        setTimeout(calculateSpeed, 300);
                    }
                }

                setTimeout(calculateSpeed, 300);

                async function push() {
                    try {
                        const { done, value } = await reader.read();
                        if (done) {
                            controller.close();
                            return;
                        }

                        const startChunkTime = performance.now();
                        chunks.push({ time: startChunkTime, length: value.length });

                        if (chunks.length > 500) {
                            chunks.shift();
                        }

                        const lengthChunks = chunks.reduce((acc, chunk, index) => {
                            if (index === 0) return acc;
                            return acc + chunk.length;
                        }, 0);

                        lastSpeed = (lengthChunks / (startChunkTime - chunks[0].time)) * 1000;

                        receivedLength += value.length;

                        controller.enqueue(value);
                        push();
                    } catch (error) {
                        if (signal?.aborted) {
                            controller.close();
                            canceled = true;
                        } else {
                            throw error;
                        }
                    }
                }

                push();
            }
        });

        const blob = await new Response(stream).blob();
        const blobUrl = URL.createObjectURL(blob);
        finished = true;

        if (canceled) {
            if (onCancel) {
                onCancel(file);
            }
            return;
        }

        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', file.fileName);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    } catch (error) {
        if (onError) onError(error);
        console.error('Error downloading file:', error);
    }
};


export async function uploadFile(file, urlStorage) {
    try {
        // Create a reference to the user's profile picture in Firebase Storage
        const storageRef = ref(storage, urlStorage);

        // Upload the file to Firebase Storage
        await uploadBytes(storageRef, file);

        // Get the download URL of the uploaded file
        return await getDownloadURL(storageRef);

    } catch (error) {
        console.error('Error updating profile picture', error);
        return null;
    }
}


export const normalize = (text) => {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

export function setDarkMode(value) {
    Cookies.set('darkMode', value, { expires: 365 * 100 });
}

export function getDefaultDarkMode() {
    const boolString = Cookies.get('darkMode') ?? 'false';
    if (boolString === 'false') {
        return false;
    } else {
        return true;
    }
}

export function checkUserGroupRole(userRoleIds, roles, roleName, group) {
    if (!auth.currentUser)
        return false;
    const userRoles = userRoleIds.map(x => roles.find(y => y.id === x)).filter(x => x);

    return group.uidCreator === auth.currentUser.uid || userRoles.some(role => {
        return role[roleName] || role.administrator
    });

    return false;
}

export function getCachedImageUrl(url) {
    if (!url) {
        return;
    }

    if (!url.includes('http')) {
        return url;
    }
    return TORRESOWO_URL + "/cache-image?url=" + encodeURIComponent(url)
}

export async function fetchGetTorresowo(url) {
    const res = await fetch(TORRESOWO_URL + url);
    return await res.json();
}

export function srt2webvtt(data) {
    // remove dos newlines
    var srt = data.replace(/\r+/g, '');
    // trim white space start and end
    srt = srt.replace(/^\s+|\s+$/g, '');

    // get cues
    var cuelist = srt.split('\n\n');
    var result = "";

    if (cuelist.length > 0) {
        result += "WEBVTT\n\n";
        for (var i = 0; i < cuelist.length; i = i + 1) {
            result += convertSrtCue(cuelist[i]);
        }
    }

    return result;
}

function convertSrtCue(caption) {
    // remove all html tags for security reasons
    //srt = srt.replace(/<[a-zA-Z\/][^>]*>/g, '');

    var cue = "";
    var s = caption.split(/\n/);

    // concatenate muilt-line string separated in array into one
    while (s.length > 3) {
        for (var i = 3; i < s.length; i++) {
            s[2] += "\n" + s[i]
        }
        s.splice(3, s.length - 3);
    }

    var line = 0;

    // detect identifier
    if (!s[0].match(/\d+:\d+:\d+/) && s[1].match(/\d+:\d+:\d+/)) {
        cue += s[0].match(/\w+/) + "\n";
        line += 1;
    }

    // get time strings
    if (s[line].match(/\d+:\d+:\d+/)) {
        // convert time string
        var m = s[1].match(/(\d+):(\d+):(\d+)(?:,(\d+))?\s*--?>\s*(\d+):(\d+):(\d+)(?:,(\d+))?/);
        if (m) {
            cue += m[1] + ":" + m[2] + ":" + m[3] + "." + m[4] + " --> "
                + m[5] + ":" + m[6] + ":" + m[7] + "." + m[8] + "\n";
            line += 1;
        } else {
            // Unrecognized timestring
            return "";
        }
    } else {
        // file format error or comment lines
        return "";
    }

    // get cue text
    if (s[line]) {
        cue += s[line] + "\n\n";
    }

    return cue;
}

export const getParentByClass = (element: HTMLElement, className: string): HTMLElement | null => {
    let parent: HTMLElement | null = element;
    while (parent && !parent.classList.contains(className)) {
        parent = parent.parentElement;
    }
    return parent;
}


export const getFlag = (lang) => {
    if (lang === "spa") {
        return "fi fi-es"
    }
    if (lang === "eng") {
        return "fi fi-gb"
    }
    if (lang === "glg") {
        return "fi fi-es-ga"
    }
    if (lang === "cat") {
        return "fi fi-es-ct"
    }
    if (lang === "jpn") {
        return "fi fi-jp"
    }
}

export const getLocalDateTimeString = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
}

export const getLocalDateString = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
}

export const getLocaleDateString = (dateString, options = undefined) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString ?? '';

    const defaultOptions: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    };

    const fmtOptions = options ?? defaultOptions;
    return new Intl.DateTimeFormat('es-ES', fmtOptions).format(date);
}

export const isFutureDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    return date.getTime() > now.getTime();
}

export const getYearOfDate = (dateString) => {
    const date = new Date(dateString);
    return date.getFullYear();
}

export const getSerieDates = (serie) => {
    try {

        if (serie.last_episode_to_air.air_date && !serie.next_episode_to_air) {
            if (getYearOfDate(serie.first_air_date) === getYearOfDate(serie.last_episode_to_air.air_date)) {
                return `${getYearOfDate(serie.first_air_date)}`;
            }
            return `${getYearOfDate(serie.first_air_date)} - ${getYearOfDate(serie.last_episode_to_air.air_date)}`;
        } else if (serie.next_episode_to_air) {
            return `${getYearOfDate(serie.first_air_date)} - Hoy`;
        } else {
            return `${getYearOfDate(serie.first_air_date)}`;
        }
    } catch {
        return '';
    }
}

export const parseSeconds = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return [hrs, mins, secs]
        .map(v => v < 10 ? "0" + v : v)
        .filter((v, i) => v !== "00" || i > 0)
        .join(":");
}

export function isHtmlEmpty(html: string): boolean {
    // Crea un elemento temporal para analizar el HTML
    const div = document.createElement('div');
    div.innerHTML = html.trim();

    // Elimina comentarios, saltos de línea y espacios innecesarios
    const cleaned = div.textContent?.trim() || '';

    // Si no hay texto visible y no hay elementos hijos con contenido
    if (cleaned.length === 0) {
        // También revisa si el HTML contiene solo etiquetas vacías
        const hasNonEmptyElements = Array.from(div.children).some(child => {
            return (child.textContent?.trim() || '').length > 0;
        });
        return !hasNonEmptyElements;
    }

    return false;
}

export function trimHtml(html: string): string {
    // Crea un elemento temporal para analizar el HTML
    const div = document.createElement('div');
    div.innerHTML = html;

    // Función recursiva para limpiar nodos
    const cleanNode = (node: ChildNode) => {
        if (node.nodeType === Node.TEXT_NODE) {
            node.textContent = node.textContent?.trim() || '';
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            // Limpia los nodos hijos primero
            Array.from(element.childNodes).forEach(child => cleanNode(child));
            // Luego elimina nodos vacíos
            Array.from(element.childNodes).forEach(child => {
                if (child.nodeType === Node.TEXT_NODE && (child.textContent?.trim() === '')) {
                    element.removeChild(child);
                } else if (child.nodeType === Node.ELEMENT_NODE) {
                    const childElement = child as HTMLElement;
                    if (childElement.innerHTML.trim() === '') {
                        element.removeChild(child);
                    }
                }
            });
        }
    };

    // Limpia el nodo raíz
    Array.from(div.childNodes).forEach(child => cleanNode(child));

    return div.innerHTML;
}


export const countItemTotalEpisodes = (item: any) => {
    if (!item.seasons?.length) return 0;

    let total = 0;

    for (const season of item.seasons) {
        if (season.season_number === 0) continue; // Ignorar temporada 0 (Especiales)
        total += season.episode_count || 0;
    }

    return total;
}

export const countItemTotalSeasons = (item: any) => {
    if (!item.seasons?.length) return 0;

    let total = 0;

    for (const season of item.seasons) {
        if (season.season_number === 0) continue; // Ignorar temporada 0 (Especiales)
        total += 1;
    }

    return total;
}
